import { AdminFormHandle } from './admin-form-handle';
import type { FormOwner } from '../public/form-handle';
import type { FormEntryExtensions, FormEntryWithOptionalOrder, FormEntryWorkflowExtensions } from 'src/framework/model/extensions/form-entry-extensions';
import type { EntityOfType, Entity, EntityChangeEventArgs } from '@cognitoforms/model.js';
import type { ObjectLookup } from '@cognitoforms/model.js/@types/helpers';
import { isEntryWithOrder } from 'src/framework/model/extensions/form-entry-extensions';
import { FormEvent } from '../eventing/form-event';
import { FormEvents } from '../public/events';
import type { EventHandler } from '@cognitoforms/model.js/@types/events';
import { closeAllToastMessages } from 'src/components/ToastMessage';
import { rebuildOrder } from 'src/framework/payment/order-builder';
import ConcurrentEntryService from 'src/web-api/concurrent-entry-service';
import EntrySet from 'src/web-api/entry-views/entry-set';
import type { EntryData, EntryKey } from 'src/web-api/entry-views/entry-view-types';
import { Deferred } from 'src/util/deferred';
import { EntryStatus } from 'src/components/EntryStatus';
import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
import type { AfterSubmitEvent } from '../public/events';
import type { WorkflowRoleMeta } from 'src/web-api/form-session';
import type { EntrySubmissionResult } from 'src/web-api/entry-service';
import { SubmissionResultStatus } from 'src/web-api/entry-service';

/**
 * Admin API for interacting with a form on the task dashboard
 */
export class TaskDashboardFormHandle extends AdminFormHandle {
	public entrySet: EntrySet;
	private _entrySetAvailable = new Deferred<EntrySet>();
	private changeHandler: EventHandler<Entity, EntityChangeEventArgs>;

	constructor(formId: string, owner: FormOwner, ready: Promise<any>) {
		super(formId, owner, ready);
		this.on(FormEvents.AfterSubmit, (event: AfterSubmitEvent) => this.augmentSubmitEvent(event));
	}

	private get entrySetAvailable() {
		return this._entrySetAvailable.promise;
	}

	get entry(): EntityOfType<FormEntryWithOptionalOrder & FormEntryExtensions & FormEntryWorkflowExtensions> {
		return this.form ? this.form.entry : null;
	}

	set entry(value: EntityOfType<FormEntryWithOptionalOrder & FormEntryExtensions & FormEntryWorkflowExtensions>) {
		this.form.entry = value;
	}

	async addChangesDetected(handler: EventHandler<Entity, EntityChangeEventArgs>) {
		this.changeHandler = handler;
		await this.enableChangeDetection();
	}

	private async enableChangeDetection() {
		await this.formAvailable;
		if (this.changeHandler) {
			this.form.formsModel.model.afterPropertySet.subscribe(this.changeHandler);
			this.form.formsModel.model.listChanged.subscribe(this.changeHandler);
		}
	}

	private async disableChangeDetection() {
		await this.formAvailable;

		// Clear the list of subscriptions
		this.form.formsModel.model.afterPropertySet.clear();
		this.form.formsModel.model.listChanged.clear();
	}

	private async getEntryJson(entryKey: EntryKey): Promise<EntryData> {
		await this.formAvailable;
		await this.entrySetAvailable;
		return this.entrySet.getEntryJson(entryKey, true);
	}

	async setExistingEntry(entryKey: EntryKey, roleId: number) : Promise<void> {
		const entryJson = await this.getEntryJson(entryKey);

		entryJson.Entry.User = this.form.session.userInfo;
		const roleName = (this.form.session.workflowRoles as WorkflowRoleMeta[]).find(workflowRole => workflowRole.Id === roleId).Name;
		entryJson.Entry.Role = roleName;
		return this.setEntry(entryJson);
	}

	private async setEntry(entryJson: EntryData) : Promise<void> {
		// Wait for the form to be available and disable change detection
		await this.disableChangeDetection();

		this.form.entryStatus = EntryStatus.Pending;

		const entryInstance = await this.form.formsModel.constructEntry<FormEntryWithOptionalOrder & FormEntryExtensions & FormEntryWorkflowExtensions>(entryJson, true);
		if (!entryInstance.meta.isNew && isEntryWithOrder(entryInstance)) {
			// Rebuild existing orders to ensure the order is up-to-date
			rebuildOrder(entryInstance, entryInstance.Order);
		}

		await this.reset(entryInstance, entryJson);

		// Wait for lookups to load before detecting changes
		await this.form.entryViewService.loadingComplete;

		// Re-enable change detection
		await this.enableChangeDetection();

		await this.form.$nextTick();

		this.form.entryStatus = EntryStatus.Ready;
	}

	public async changeView(viewId: string, token: string, refresh: boolean = true): Promise<void> {
		await this.formAvailable;
		if (!this.form || !this.form.entryViewService) {
			return;
		}

		this.form.entryViewService.registerViewToken(viewId, token);

		if (this.form.entryService instanceof ConcurrentEntryService)
			this.form.entryService.registerView(viewId, ViewType.Form);

		this.entrySet = await EntrySet.get(this.form.entryViewService, this.form.formsModel.entryTypeName, viewId);
		this._entrySetAvailable.resolve(this.entrySet);
		if (refresh) this.entrySet.refresh();
	}

	private reset(entry: EntityOfType<FormEntryWithOptionalOrder & FormEntryExtensions & FormEntryWorkflowExtensions>, initialEntryJson: ObjectLookup<any>) {
		if (this.form.quantityService) {
			const hasInitialState = !entry.meta.isNew && entry.Entry.Status !== 'Incomplete';
			this.form.quantityService.reset({ hasInitialState: hasInitialState, root: entry });
		}

		if (this.form.entryService instanceof ConcurrentEntryService)
			this.form.entryService.updateInitialEntryJson(initialEntryJson);

		// Set the current action to the first allowed action, if any
		if (this.form.allowedActions.length)
			this.form.entry.Entry.Action = this.form.allowedActions[0].ActionName;
		else
			this.form.entry.Entry.Action = null;

		closeAllToastMessages();

		return new Promise(resolve => {
			this.form.withTransitionsDisabled(async () => {
				this.entry = entry;
				this.form.log.registerEntry(entry);
				await this.emit(new FormEvent(FormEvents.ResetEntry));
				if (this.form.quantityService)
					await this.form.quantityService.refresh();
				resolve(entry);
			});
		});
	}

	async performAction(action: string) {
		await this.formAvailable;
		const submission = this.form.submit({ action });
		submission.then((res: EntrySubmissionResult) => {
			if (res && res.status === SubmissionResultStatus.Success)
				this.reset(this.entry, res.entry);
		});
		return submission;
	}

	private augmentSubmitEvent(event: AfterSubmitEvent) {
		const actionName = event.data.entry.Entry.Action;
		event.data.convertedMessage = this.getConfirmationMessage(actionName);
	}
}
